import { Link } from 'react-router-dom'
import type { Generation } from '../types'

interface GenerationCardProps {
  generation: Generation
  onQuickView?: (generation: Generation) => void
  onMoveToProject?: (generation: Generation) => void
  onDelete?: (generation: Generation) => void
  selectionMode?: boolean
  isSelected?: boolean
  onToggleSelect?: (id: string) => void
}

export function GenerationCard({
  generation,
  onQuickView,
  onMoveToProject,
  onDelete,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
}: GenerationCardProps) {
  const isGenerating = generation.status === 'GENERATING'
  const isFailed = generation.status === 'FAILED'

  // Handle download
  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (generation.thumbnail) {
      const link = document.createElement('a')
      link.href = generation.thumbnail
      link.download = `kalypso-${generation.id}.png`
      link.click()
    }
  }

  // Handle move to project
  const handleMoveToProject = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onMoveToProject?.(generation)
  }

  // Handle delete
  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onDelete?.(generation)
  }

  if (isGenerating) {
    return (
      <div className="relative group aspect-square rounded-xl overflow-hidden bg-white border-2 border-dashed border-primary/30 flex flex-col items-center justify-center p-4 text-center shadow-inner">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent animate-pulse"></div>
        {onDelete && (
          <button
            onClick={handleDelete}
            className="absolute top-2 right-2 z-20 size-7 rounded-full bg-white/80 hover:bg-red-100 text-text-muted hover:text-red-600 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 shadow-sm"
            title="Cancel generation"
          >
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        )}
        <div className="bg-primary/10 rounded-full p-2 mb-2 animate-spin-glow">
          <span className="material-symbols-outlined text-2xl text-primary">autorenew</span>
        </div>
        <p className="text-xs font-bold text-primary-dark relative z-10">Generating...</p>
        <p className="text-[10px] text-text-muted relative z-10 mt-1 truncate max-w-[80%]">
          {generation.productDescription || 'New image'}
        </p>
      </div>
    )
  }

  if (isFailed) {
    return (
      <div className="relative aspect-square rounded-xl border border-red-200 bg-red-50 flex flex-col items-center justify-center gap-2">
        <span className="material-symbols-outlined text-3xl text-red-400">error</span>
        <span className="text-sm text-red-600">Failed</span>
        {onDelete && (
          <button
            onClick={handleDelete}
            className="mt-2 px-3 py-1 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 text-xs font-medium transition-colors flex items-center gap-1"
            title="Delete"
          >
            <span className="material-symbols-outlined text-sm">delete</span>
            Delete
          </button>
        )}
      </div>
    )
  }

  return (
    <Link
      to={`/gallery/${generation.id}`}
      className={`relative group aspect-square rounded-xl overflow-hidden bg-gray-100 shadow-sm transition-all duration-200 ${
        isSelected ? 'ring-2 ring-primary ring-offset-2' : ''
      }`}
    >
      {/* Selection Checkbox - show on hover or when in selection mode */}
      {onToggleSelect && (
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onToggleSelect(generation.id)
          }}
          className={`absolute top-2 left-2 z-20 w-5 h-5 rounded-md border-2
            flex items-center justify-center transition-all duration-200
            ${selectionMode ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
            ${isSelected
              ? 'bg-primary border-primary text-white animate-[check-pop_0.2s_ease-out]'
              : 'bg-white/90 border-border-dark hover:border-primary'
            }`}
        >
          {isSelected && (
            <span className="material-symbols-outlined text-xs font-bold">check</span>
          )}
        </button>
      )}

      {/* Image */}
      {generation.thumbnail ? (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${generation.thumbnail}")` }}
          data-alt={generation.productDescription || 'Generated image'}
        />
      ) : (
        <div className="absolute inset-0 bg-surface-muted flex items-center justify-center">
          <span className="material-symbols-outlined text-4xl text-text-light">image</span>
        </div>
      )}

      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
        {/* Top Row - Project Badge (left) and Status Badge (right) */}
        <div className="flex justify-between items-start action-reveal">
          {/* Project Badge */}
          {generation.projectName && (
            <span className="px-2 py-1 rounded bg-white/90 text-text-main text-[10px] font-medium truncate max-w-[120px] shadow-sm ml-6">
              {generation.projectName}
            </span>
          )}
          {!generation.projectName && <div />}
          {/* Status Badge */}
          <span className="px-2 py-1 rounded bg-green-100 text-green-800 text-[10px] font-bold uppercase tracking-wider shadow-sm">
            Completed
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-2 mb-2 action-reveal" style={{ animationDelay: '0.05s' }}>
          {onQuickView && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onQuickView(generation)
              }}
              className="size-9 rounded-full bg-white text-text-main hover:text-primary hover:scale-105 flex items-center justify-center transition-all shadow-lg"
              title="Quick view"
            >
              <span className="material-symbols-outlined text-sm">fullscreen</span>
            </button>
          )}
          <button
            onClick={handleDownload}
            className="size-9 rounded-full bg-white text-text-main hover:text-primary hover:scale-105 flex items-center justify-center transition-all shadow-lg"
            title="Download"
          >
            <span className="material-symbols-outlined text-sm">download</span>
          </button>
          {onMoveToProject && (
            <button
              onClick={handleMoveToProject}
              className="size-9 rounded-full bg-white text-text-main hover:text-primary hover:scale-105 flex items-center justify-center transition-all shadow-lg"
              title="Move to project"
            >
              <span className="material-symbols-outlined text-sm">drive_file_move</span>
            </button>
          )}
        </div>
      </div>
    </Link>
  )
}
